import { useMemo } from "react";
import type { FeatureCollection, Geometry, Point } from "geojson";
import type { GeoJSONSource } from "mapbox-gl";
import {
	MapHelperInstance,
	useStyleDependantMapEffect,
	useStyleSetupMapEffect,
} from "../../helper/map-helper";
import type {
	MappedinMVFFloor,
	MappedinMVFGeometryProperties,
	MappedinMVFLabelProperties,
} from "../../helper/mappedin/mappedin-mvf";
import { geoJSONBlankCollection } from "./mapbox-injectable";
import whitelabel from "../../whitelabel";

const mapboxIDMappedinPrefix = "mappedin-floorplan-";
const mapboxIDMappedinGeometry = mapboxIDMappedinPrefix + "geometry";
const mapboxIDMappedinLabels = mapboxIDMappedinPrefix + "labels";

export interface MapboxInjectableMappedinFloorplansProps {
	floors: MappedinMVFFloor[];
	belowLayer?: string;
}

const defaultMappedinLabelTextFont: string[] = [
	"Poppins",
	"Open Sans Regular",
	"Arial Unicode MS Regular",
];

function buildMappedinGeometryCollection(
	floors: MappedinMVFFloor[],
): FeatureCollection<Geometry, MappedinMVFGeometryProperties> {
	return {
		type: "FeatureCollection",
		features: floors.flatMap((floor) => floor.geometry.features),
	};
}

function buildMappedinLabelCollection(
	floors: MappedinMVFFloor[],
): FeatureCollection<Point, MappedinMVFLabelProperties> {
	return {
		type: "FeatureCollection",
		features: floors.flatMap((floor) => floor.labels.features),
	};
}

export default function useMapboxInjectableMappedinFloorplans(
	mapInstance: MapHelperInstance,
	props: MapboxInjectableMappedinFloorplansProps,
): string | undefined {
	const geometryData = useMemo(
		() => buildMappedinGeometryCollection(props.floors),
		[props.floors],
	);
	const labelData = useMemo(
		() => buildMappedinLabelCollection(props.floors),
		[props.floors],
	);

	useStyleSetupMapEffect(mapInstance, (_, box) => {
		box.addSource(mapboxIDMappedinLabels, {
			type: "geojson",
			data: geoJSONBlankCollection,
		});
		box.addLayer(
			{
				id: mapboxIDMappedinLabels,
				type: "symbol",
				source: mapboxIDMappedinLabels,
				layout: {
					"text-field": ["get", "text"],
					"text-font":
						whitelabel.mappedin?.textFont ??
						defaultMappedinLabelTextFont,
					"text-size": 12,
					"text-anchor": "center",
					"text-allow-overlap": false,
					"text-max-width": 12,
				},
				paint: {
					"text-color": "#1C2635",
					"text-halo-color": "#FFFFFF",
					"text-halo-width": 1.25,
					"text-opacity": 0.95,
				},
			},
			props.belowLayer,
		);

		box.addSource(mapboxIDMappedinGeometry, {
			type: "geojson",
			data: geoJSONBlankCollection,
		});
		box.addLayer(
			{
				id: mapboxIDMappedinGeometry,
				type: "fill-extrusion",
				source: mapboxIDMappedinGeometry,
				paint: {
					"fill-extrusion-color": ["get", "color"],
					"fill-extrusion-height": ["get", "height"],
					"fill-extrusion-base": ["get", "altitude"],
					"fill-extrusion-ambient-occlusion-intensity": 0.4,
				},
			},
			mapboxIDMappedinLabels,
		);
	});

	useStyleDependantMapEffect(
		mapInstance,
		(map) => {
			const geometrySource = map.getSource(
				mapboxIDMappedinGeometry,
			) as GeoJSONSource;
			geometrySource.setData(geometryData);
		},
		[geometryData],
	);

	useStyleDependantMapEffect(
		mapInstance,
		(map) => {
			const labelSource = map.getSource(
				mapboxIDMappedinLabels,
			) as GeoJSONSource;
			labelSource.setData(labelData);
		},
		[labelData],
	);

	return mapboxIDMappedinLabels;
}
